/**
 * OrganizationDetails Page - Salesforce SLDS Style
 *
 * Страница карточки организации в стиле Salesforce Lightning Record Page.
 * - Тёмно-синяя шапка с полями
 * - Progress Path на всю ширину
 * - Двухколоночный layout с виджетами
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Text,
  Button,
  Loader,
  Center,
  Grid
} from '@mantine/core';
import tokens from '../theme/designTokens';
import { useSession } from '../context/SessionContext';

// RecordPage компоненты
import { RecordHeader, ProgressPath } from '../components/Organizations/RecordPage';

// Widgets
import DetailsWidget from '../components/Organizations/widgets/DetailsWidget';
import CalendarWidget from '../components/Organizations/widgets/CalendarWidget';
import ContactsWidget from '../components/Organizations/widgets/ContactsWidget';
import MessagesWidget from '../components/Organizations/widgets/MessagesWidget';
import MeetingsWidget from '../components/Organizations/widgets/MeetingsWidget';
import MasterClassesWidget from '../components/Organizations/widgets/MasterClassesWidget';
import StudyGroupsWidget from '../components/Organizations/widgets/StudyGroupsWidget';
import ScheduleWidget from '../components/Organizations/widgets/ScheduleWidget';
import RequisitesWidget from '../components/Organizations/widgets/RequisitesWidget';
import CallsWidget from '../components/Organizations/widgets/CallsWidget';
import ActivityWidget from '../components/Organizations/widgets/ActivityWidget';
import GroupMembersWidget from '../components/Organizations/widgets/GroupMembersWidget';

/**
 * Основной компонент страницы
 */
const OrganizationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Счётчик для триггера обновления истории активности
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Счётчик для триггера обновления требований этапа (ProgressPath)
  const [stageRequirementsRefreshKey, setStageRequirementsRefreshKey] = useState(0);

  // Callback для обновления истории активности и требований этапа
  const refreshActivity = useCallback(() => {
    setActivityRefreshKey((prev) => prev + 1);
    // Также обновляем требования этапа, т.к. добавление сообщений/звонков/встреч влияет на них
    setStageRequirementsRefreshKey((prev) => prev + 1);
  }, []);

  // Callback для обновления организации
  const handleOrganizationUpdate = useCallback((updatedOrg) => {
    setOrganization(updatedOrg);
    refreshActivity();
    // Обновляем требования этапа при изменении организации (назначение менеджера/сейла)
    setStageRequirementsRefreshKey((prev) => prev + 1);
  }, [refreshActivity]);

  // Загрузка данных
  useEffect(() => {
    const fetchOrganization = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/organizations/${id}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Организация не найдена');
          }
          throw new Error('Не удалось загрузить данные организации');
        }

        const data = await response.json();
        setOrganization(data);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [id]);

  const handleBack = () => {
    navigate('/organizations');
  };

  const handleStageChange = (newStageCode, newStageName) => {
    // Обновляем локальное состояние организации
    setOrganization(prev => ({
      ...prev,
      cooperation_stage: newStageCode
    }));
    // Обновляем историю активности
    refreshActivity();
  };

  // Loading state
  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  // Error state
  if (error) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Text c="red" size="lg">{error}</Text>
          <Button onClick={handleBack} variant="light">
            Вернуться к списку
          </Button>
        </Stack>
      </Center>
    );
  }

  // Not found state
  if (!organization) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Text c="dimmed" size="lg">Организация не найдена</Text>
          <Button onClick={handleBack} variant="light">
            Вернуться к списку
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ backgroundColor: tokens.colors.neutral[1], minHeight: '100vh' }}>
      {/* Record Header */}
      <RecordHeader
        organization={organization}
        currentUser={user}
        onBack={handleBack}
        onOrganizationUpdate={handleOrganizationUpdate}
      />

      {/* Progress Path - на всю ширину */}
      <ProgressPath
        organizationId={organization.id}
        currentStageCode={organization.cooperation_stage || 'new'}
        onStageChange={handleStageChange}
        showNextButton={true}
        refreshKey={stageRequirementsRefreshKey}
        userRole={user?.role}
        organizationType={organization.organization_type || 'b2b'}
      />

      {/* Main Content */}
      <Box px="md" py="md">
        <Grid gutter="md">
          {/* Левая колонка - Related Lists */}
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="md">
              {/* Компании группы (только для групп компаний) */}
              <GroupMembersWidget organization={organization} onActivityChange={refreshActivity} />

              {/* Календарь */}
              <CalendarWidget organization={organization} />

              {/* Контакты */}
              <ContactsWidget organization={organization} onActivityChange={refreshActivity} />

              {/* Виджеты, скрытые для Individual организаций */}
              {organization.organization_type !== 'individual' && (
                <>
                  {/* Сообщения */}
                  <MessagesWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Звонки */}
                  <CallsWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Встречи */}
                  <MeetingsWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Мастер-классы */}
                  <MasterClassesWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Учебные группы */}
                  <StudyGroupsWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Расписание занятий */}
                  <ScheduleWidget organization={organization} onActivityChange={refreshActivity} />

                  {/* Реквизиты и Договоры */}
                  <RequisitesWidget organization={organization} onActivityChange={refreshActivity} />
                </>
              )}
            </Stack>
          </Grid.Col>

          {/* Правая колонка - Details и Activity */}
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack gap="md">
              <DetailsWidget organization={organization} onStageChange={handleStageChange} onOrganizationUpdate={handleOrganizationUpdate} currentUser={user} />
              <ActivityWidget organization={organization} refreshKey={activityRefreshKey} />
            </Stack>
          </Grid.Col>
        </Grid>
      </Box>
    </Box>
  );
};

export default OrganizationDetails;
